import Spinner from "@/components/common/Spinner";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowRight, type LucideIcon } from "lucide-react";
import { useEffect, useState } from "react";
import NextButton from "../form/NextButton";

interface ButtonConfig {
	text: string;
	to: string;
	params?: Record<string, string>;
	search?: Record<string, string>;
}

interface CompleteProps {
	// Content
	title: string;
	description: string;
	icon?: LucideIcon;

	// Loading state
	isLoading?: boolean;
	loadingText?: string;
	loadingDescription?: string;
	minLoadingTime?: number;

	// Buttons
	primaryButton: ButtonConfig;
	secondaryButton?: ButtonConfig & {
		showWhen?: boolean;
	};
}

export function Complete({
	title,
	description,
	icon: Icon,
	isLoading: externalIsLoading,
	loadingText = "Almost there!",
	loadingDescription = "Just a moment...",
	minLoadingTime = 1000,
	primaryButton,
	secondaryButton,
}: CompleteProps) {
	const navigate = useNavigate();
	const [isMinLoading, setIsMinLoading] = useState(true);

	useEffect(() => {
		const timer = setTimeout(() => {
			setIsMinLoading(false);
		}, minLoadingTime);

		return () => clearTimeout(timer);
	}, [minLoadingTime]);

	const isLoading = externalIsLoading || isMinLoading;

	return (
		<div className="flex flex-col mx-auto py-12 max-w-md">
			<div className="flex flex-col flex-1 justify-center items-center gap-y-8 px-4">
				<div className="space-y-4 text-center">
					<h1 className="bg-clip-text bg-gradient-to-r from-sky-600 to-pink-600 font-semibold text-transparent text-3xl transition-all duration-500 ease-in-out">
						{isLoading ? loadingText : title}
					</h1>
					<div className="transition-all duration-500 ease-in-out">
						{(isLoading ? loadingDescription : description) && (
							<p className="text-slate-600 text-lg">
								{isLoading ? loadingDescription : description}
							</p>
						)}
					</div>
				</div>

				<div className="relative rounded-[1rem] w-40 aspect-square transition-all duration-500 ease-in-out">
					<div className="absolute inset-0 bg-gradient-to-br from-pink-300/40 via-indigo-300/40 to-sky-300/40 shadow-lg border border-white/20 rounded-[1rem] transition-all duration-500 ease-in-out" />
					<div className="absolute inset-0 bg-gradient-to-br from-white/60 to-transparent rounded-[1rem] transition-all duration-500 ease-in-out" />
					<div className="absolute inset-0 flex justify-center items-center">
						<div
							className={`absolute inset-0 flex justify-center items-center transition-opacity duration-500 ease-in-out ${
								isLoading ? "opacity-100" : "opacity-0"
							}`}
						>
							<Spinner className="w-16 h-16" />
						</div>
						{Icon && (
							<div
								className={`absolute inset-0 flex justify-center items-center transition-opacity duration-500 ease-in-out ${
									isLoading ? "opacity-0" : "opacity-100"
								}`}
							>
								<div className="relative">
									<div className="absolute inset-0 bg-gradient-to-br from-indigo-500/30 to-pink-500/30 blur-xl" />
									<Icon className="relative drop-shadow-lg w-16 h-16 text-indigo-600" />
								</div>
							</div>
						)}
					</div>
				</div>

				<div
					className={`flex flex-col gap-4 pt-4 w-full max-w-md transition-all duration-500 ease-in-out ${
						isLoading ? "opacity-0 translate-y-4" : "opacity-100 translate-y-0"
					}`}
				>
					<NextButton onClick={() => navigate({ to: primaryButton.to })} />

					{secondaryButton?.showWhen && (
						<Button
							asChild
							variant="outline"
							size="lg"
							className="group flex justify-center items-center gap-2 w-full"
						>
							<Link
								data-testid="secondary-button"
								to={secondaryButton.to}
								params={secondaryButton.params}
								search={secondaryButton.search}
							>
								{secondaryButton.text}
							</Link>
						</Button>
					)}
				</div>
			</div>
		</div>
	);
}
